// A button that opens a panel under it. A click outside the panel, or
// Escape, closes it. The compact layout shows the panel in place and hides
// the button, so the class on the root is the only state.

import { OWN_SOUND_ATTRIBUTE } from '../constants';
import { el } from '../lib/dom';
import { playSound } from '../sound/player';

const OPEN_CLASS = 'menu--open';

/**
 * `icon` stands for the word where the styles hide it: the top bar of the phone.
 * `closeOn` gives the owner a way to close the panel, when an action in it takes the player elsewhere.
 */
export const createMenu = (
  label: string,
  children: readonly HTMLElement[],
  icon?: SVGElement,
  closeOn?: (close: () => void) => void,
): HTMLElement => {
  const toggle = el(
    'button',
    {
      className: 'button button--pass-through menu__toggle',
      type: 'button',
      ariaLabel: label,
      ariaExpanded: 'false',
    },
    [icon, el('span', { className: 'button__label', textContent: label })],
  );
  const panel = el('div', { className: 'menu__panel' }, children);
  const root = el('div', { className: 'menu' }, [toggle, panel]);

  // The panel has its own two sounds, so the button has no press sound.
  toggle.setAttribute(OWN_SOUND_ATTRIBUTE, '');

  const setOpen = (open: boolean): void => {
    if (open !== root.classList.contains(OPEN_CLASS)) playSound(open ? 'menuOpen' : 'menuFold');
    root.classList.toggle(OPEN_CLASS, open);
    toggle.ariaExpanded = String(open);
  };

  toggle.onclick = () => setOpen(!root.classList.contains(OPEN_CLASS));
  document.addEventListener('pointerdown', (event) => {
    if (event.target instanceof Node && !root.contains(event.target)) setOpen(false);
  });
  root.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    setOpen(false);
    toggle.focus();
  });
  closeOn?.(() => setOpen(false));

  return root;
};
